import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { usePlacesWidget } from 'react-google-autocomplete';

// import Heading from 'components/shared/heading';
import Input from 'components/shared/input';
import Select from 'components/shared/select';
import Button from 'components/shared/button';

import styles from './styles.module.scss';
import { BUILDING_TYPES } from '../../../config';

// Validation schema for Building Contact
const validationSchema = yup.object().shape({
  buildingType: yup.string().required('Building type is required'),
  buildingTypeOther: yup.string().when('buildingType', {
    is: 'other',
    then: yup.string().required('Please specify building type'),
  }),
  firstName: yup.string().trim().required('First name is required'),
  lastName: yup.string().trim().required('Last name is required'),
  contactEmail: yup.string().trim().required('Email is required').email('Must be a valid email'),
  phone: yup.string().trim().required('Phone number is required'),
  buildingAddress: yup.string().trim(),
});

const BuildingContact = ({ hint = '', initialData, onComplete, onBack }) => {
  const [buildingAddress, setBuildingAddress] = useState('');
  const [addressError, setAddressError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initialData,
    mode: 'onChange',
  });

  const watchedValues = watch();

  // Google Places autocomplete
  const { ref: addressRef } = usePlacesWidget({
    apiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    componentRestrictions: { country: 'us' },
    options: {
      types: ['geocode', 'establishment'],
    },
    onPlaceSelected: (place) => {
      setBuildingAddress(place.formatted_address?.replace(', USA', ''));
      setAddressError(false);
    },
  });

  const onSubmit = useCallback(
    (data) => {
      if (!buildingAddress) {
        setAddressError(true);
        return;
      }

      onComplete({ ...data, buildingAddress });
    },
    [buildingAddress, onComplete]
  );

  return (
    <div className={styles.buildingContact} id="estimate-building-contact">
      {/* <Heading className={styles.stepTitle} tag="h3" size="md">
        Building & Contact Information
      </Heading> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="firstName">
              First Name *
            </label>
            <Input
              {...register('firstName')}
              id="firstName"
              placeholder="First name..."
              error={errors.firstName?.message}
              withBorder
              requiredStyle
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="lastName">
              Last Name *
            </label>
            <Input
              {...register('lastName')}
              id="lastName"
              placeholder="Last name..."
              error={errors.lastName?.message}
              withBorder
              requiredStyle
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="email">
              Email *
            </label>
            <Input
              {...register('contactEmail')}
              id="email"
              type="text"
              placeholder="Email address..."
              error={errors.contactEmail?.message}
              withBorder
              requiredStyle
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="phone">
              Phone *
            </label>
            <Input
              {...register('phone')}
              id="phone"
              type="tel"
              placeholder="Phone number..."
              error={errors.phone?.message}
              withBorder
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="buildingType">
              Building Type *
            </label>
            <Select
              {...register('buildingType')}
              id="buildingType"
              withBorder
              error={errors.buildingType?.message}
            >
              <option value="">Select building type...</option>
              {BUILDING_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          </div>

          {watchedValues.buildingType === 'other' && (
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="buildingTypeOther">
                What type of building is it? *
              </label>
              <Input
                {...register('buildingTypeOther')}
                id="buildingTypeOther"
                placeholder="Please specify..."
                error={errors.buildingTypeOther?.message}
                withBorder
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="buildingAddress">
              Building Address
            </label>
            <Input
              ref={addressRef}
              id="buildingAddress"
              name="buildingAddress"
              placeholder="Start typing address..."
              helperText="Optional but helps personalize your estimate and show nearby reference installs."
              withBorder
              requiredStyle
              error={addressError ? 'Building address is required' : undefined}
            />
          </div>
        </div>

        {hint && <div className={styles.hint}>{hint}</div>}

        <div className={styles.navigation}>
          <Button theme="secondary" type="button" onClick={onBack}>
            Back
          </Button>
          <Button theme="accent-primary" type="submit">
            Next
          </Button>
        </div>
      </form>
    </div>
  );
};

BuildingContact.propTypes = {
  hint: PropTypes.string,
  initialData: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default BuildingContact;
